'use client'
import React, { useState } from 'react';
import styled from 'styled-components';
import { useConfig } from '@/contexts/ConfigContext';
import { useLanguage } from '@/contexts/LanguageContext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartValueInput from '@/components/CustomInput/SmartValueInput';

const PageWrapper = styled.div`
    width: 100%;
    max-width: 1000px;
    padding-right: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    animation: fadeIn 0.5s ease-in-out;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const SectionHeader = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
`;

const Title = styled.h2`
    font-size: 1.5rem;
    color: #fff;
    font-family: 'Chakra Petch', sans-serif;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 0;
    background: linear-gradient(180deg, #fff 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.3);
    max-width: 600px;
    margin-bottom: 10px;
`;

const PreviewContainer = styled.div`
    width: 100%;
    //background: rgba(15, 23, 42, 0.6);
    //border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 12px;
    padding: 0px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    backdrop-filter: blur(10px);
`;

const PreviewLabel = styled.span`
    font-size: 0.75rem;
    color: #a78bfa;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const PreviewBox = styled.div`
position: relative;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    font-family: 'JetBrains Mono', monospace;
    color: #ff4ab4;
    font-size: 0.8rem;
    word-break: break-all;
    line-height: 30px;
    width: 100%;
    min-height: 50px;
    padding: 12px 130px 12px 16px;
`;

const CopyButton = styled.button`
    position: absolute;
    top: 12px;
    right: 14px;
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a78bfa;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;

    &:hover {
        background: rgba(99, 102, 241, 0.2);
        color: #fff;
    }
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
`;

const SectionBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const CheckboxGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
`;

const VideoGrid = styled.div`
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 4px;
`;

const SectionTitle = styled.h4`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #a78bfa;
    font-weight: 600;
    font-family: var(--font-chakra);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(167, 139, 250, 0.4), transparent);
    }
`;

const SettingsGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: rgba(0, 0, 0, 0.25);
    padding: 12px;
    width: 100%;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
`;

const ControlRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-height: 42px;
    padding: 10px 15px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.03);
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(167, 139, 250, 0.2);
    }
`;

const ControlLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LabelText = styled.span`
    font-size: 0.75rem;
    font-weight: 400;
    color: #f8fafc;
    font-family: var(--font-roboto);
`;

const Checkbox = styled.div`
    width: 22px;
    height: 22px;
    border: 2px solid #666;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: ${({ $checked }) => ($checked ? '#6366f1' : 'transparent')};
    border-color: ${({ $checked }) => ($checked ? '#6366f1' : '#666')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${({ $checked }) => ($checked ? '0 0 10px rgba(99, 102, 241, 0.3)' : 'none')};
    flex-shrink: 0;

    &:hover {
        border-color: #6366f1;
    }
`;

const SelectWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledSelect = styled.select`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 7px 32px 7px 12px;
    color: #fff;
    font-size: 0.78rem;
    outline: none;
    font-family: 'JetBrains Mono', monospace;
    cursor: pointer;
    appearance: none;
    transition: all 0.2s ease;
    min-width: 170px;

    &:hover {
        border-color: #6366f1;
        background: rgba(99, 102, 241, 0.08);
    }

    &:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }

    option {
        background: #0f172a;
        color: white;
    }
`;

const SelectArrow = styled(ExpandMoreIcon)`
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: rgba(255, 255, 255, 0.5);
    font-size: 1.1rem !important;
`;

const TextArea = styled.textarea`
    width: 100%;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 13px;
    color: #fff;
    font-size: 0.85rem;
    font-family: 'JetBrains Mono', monospace;
    outline: none;
    resize: vertical;
    transition: all 0.2s ease;
    height: 44px;

    &:hover {
        border-color: rgba(167, 139, 250, 0.3);
    }

    &:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.2);
    }
`;

const CustomArgsHint = styled.span`
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.35);
    padding: 4px 4px 0;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(8px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.2s ease-out;
`;

const ModalContent = styled.div`
    background: #0f172a;
    border: 1px solid rgba(99, 102, 241, 0.2);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    padding: 32px;
    border-radius: 20px;
    width: 420px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.5), transparent);
    }
`;

const ModalTitle = styled.h3`
    margin: 0;
    color: #fff;
    font-size: 1.4rem;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    text-align: center;
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 4px;
`;

const ModalButton = styled.button`
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    border: none;
    transition: all 0.2s;
    font-family: inherit;
    font-size: 0.9rem;

    ${({ $primary }) => $primary ? `
        background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
        }
    ` : `
        background: rgba(255, 255, 255, 0.05);
        color: rgba(255, 255, 255, 0.8);
        &:hover {
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
        }
    `}
`;

const ModalInput = styled.input`
    width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 10px 12px;
    color: #fff;
    font-size: 0.9rem;
    font-family: 'JetBrains Mono', monospace;
    outline: none;
    transition: all 0.2s;

    &:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }
`;

const ModalLabel = styled.label`
    display: block;
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const CheckboxOption = ({ label, description, checked, onChange }) => (
    <ControlRow>
        <ControlLabel>
            <LabelText>{label}</LabelText>
            {description && (
                <Tooltip title={description} placement="right" arrow>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <HelpOutlineIcon style={{ fontSize: 14, opacity: 0.3, cursor: 'help', color: '#fff' }} />
                    </span>
                </Tooltip>
            )}
        </ControlLabel>
        <Checkbox $checked={checked} onClick={() => onChange(!checked)}>
            {checked && <CheckIcon style={{ fontSize: 13, color: 'white' }} />}
        </Checkbox>
    </ControlRow>
);

const RESOLUTIONS = [
    { label: '1920 x 1080 (FHD)', w: 1920, h: 1080 },
    { label: '1440 x 1080 (4:3)', w: 1440, h: 1080 },
    { label: '1280 x 960 (4:3)', w: 1280, h: 960 },
    { label: '1280 x 720 (HD)', w: 1280, h: 720 },
    { label: '1024 x 768 (XGA)', w: 1024, h: 768 },
];

const LaunchOptionsPage = () => {
    const { t } = useLanguage();
    const { configData, updateConfig } = useConfig();
    const [copied, setCopied] = useState(false);
    const [showCustomRes, setShowCustomRes] = useState(false);
    const [tempW, setTempW] = useState(1920);
    const [tempH, setTempH] = useState(1080);

    const data = configData?.launch_options || {
        novid: true,
        high: true,
        console: true,
        width: 1920,
        height: 1080,
        refresh: 144,
        exec_autoexec: true,
        fullscreen: false,
        custom_args: ''
    };

    const update = (key, value) => {
        updateConfig('launch_options', key, value);
    };

    const generatedString = React.useMemo(() => {
        const parts = [];
        if (data.novid) parts.push('-novid');
        if (data.high) parts.push('-high');
        if (data.console) parts.push('-console');
        if (data.fullscreen) parts.push('-fullscreen');
        if (data.width && data.height) parts.push(`-w ${data.width} -h ${data.height}`);
        if (data.refresh) parts.push(`-refresh ${data.refresh}`);
        if (data.custom_args) parts.push(data.custom_args);
        if (data.exec_autoexec) parts.push('+exec autoexec.cfg');
        return parts.join(' ');
    }, [data]);

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleResolutionChange = (e) => {
        const val = e.target.value;
        if (val === 'custom') {
            setTempW(data.width || 1920);
            setTempH(data.height || 1080);
            setShowCustomRes(true);
        } else {
            const [w, h] = val.split('x').map(Number);
            update('width', w);
            update('height', h);
        }
    };

    const saveCustomRes = () => {
        update('width', parseInt(tempW) || 1920);
        update('height', parseInt(tempH) || 1080);
        setShowCustomRes(false);
    };

    const currentResValue = RESOLUTIONS.some(r => r.w === data.width && r.h === data.height)
        ? `${data.width}x${data.height}`
        : 'custom';

    return (
        <PageWrapper>
            {showCustomRes && (
                <ModalOverlay onClick={() => setShowCustomRes(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalTitle>Custom Resolution</ModalTitle>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <ModalLabel>Width</ModalLabel>
                                <ModalInput
                                    type="number"
                                    value={tempW}
                                    onChange={e => setTempW(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <ModalLabel>Height</ModalLabel>
                                <ModalInput
                                    type="number"
                                    value={tempH}
                                    onChange={e => setTempH(e.target.value)}
                                />
                            </div>
                        </div>
                        <ModalActions>
                            <ModalButton onClick={() => setShowCustomRes(false)}>Cancel</ModalButton>
                            <ModalButton $primary onClick={saveCustomRes}>Apply</ModalButton>
                        </ModalActions>
                    </ModalContent>
                </ModalOverlay>
            )}

            

            <PreviewContainer>
                <PreviewLabel>{t('STEAM_LAUNCH_STRING_LABEL')}</PreviewLabel>
                <SettingsGroup>
                        <CustomArgsHint>Add any other launch options here:</CustomArgsHint>
                        <TextArea
                            placeholder="-freq 144 -threads 4 ..."
                            value={data.custom_args}
                            onChange={(e) => update('custom_args', e.target.value)}
                        />
                    </SettingsGroup>
                <PreviewBox>
                    {generatedString}
                    <CopyButton onClick={handleCopy}>
                    {copied
                        ? <CheckIcon style={{ fontSize: 14 }} />
                        : <ContentCopyIcon style={{ fontSize: 14 }} />}
                    {copied ? t('LAUNCH_OPTIONS_COPIED') : t('LAUNCH_OPTIONS_COPY')}
                </CopyButton>
                </PreviewBox>
                
            </PreviewContainer>
            <SectionHeader>
                <Subtitle>{t('LAUNCH_OPTIONS_DESC')}</Subtitle>
            </SectionHeader>

            <OptionsContainer>
                <SectionBlock>
                    <SectionTitle>{t('GENERAL')}</SectionTitle>
                    <SettingsGroup>
                        <CheckboxGrid>
                            <CheckboxOption
                                label="Skip Intro Video (-novid)"
                                description={t('CMD_NOVID_DESC')}
                                checked={data.novid}
                                onChange={(val) => update('novid', val)}
                            />
                            <CheckboxOption
                                label="High Priority (-high)"
                                description={t('CMD_HIGH_DESC')}
                                checked={data.high}
                                onChange={(val) => update('high', val)}
                            />
                            <CheckboxOption
                                label="Enable Console (-console)"
                                description={t('CMD_CONSOLE_DESC')}
                                checked={data.console}
                                onChange={(val) => update('console', val)}
                            />
                            <CheckboxOption
                                label="Exec Autoexec (+exec autoexec.cfg)"
                                checked={data.exec_autoexec}
                                onChange={(val) => update('exec_autoexec', val)}
                            />
                        </CheckboxGrid>
                    </SettingsGroup>
                </SectionBlock>

                <SectionBlock>
                    <SectionTitle>Video & Display</SectionTitle>
                    <SettingsGroup>
                        <VideoGrid>
                            <ControlRow>
                                <ControlLabel>
                                    <LabelText>Resolution</LabelText>
                                    <Tooltip title={t('CMD_RESO_DESC')} placement="right" arrow>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            <HelpOutlineIcon style={{ fontSize: 14, opacity: 0.3, cursor: 'help', color: '#fff' }} />
                                        </span>
                                    </Tooltip>
                                </ControlLabel>
                                <SelectWrapper>
                                    <StyledSelect value={currentResValue} onChange={handleResolutionChange}>
                                        {RESOLUTIONS.map(r => (
                                            <option key={`${r.w}x${r.h}`} value={`${r.w}x${r.h}`}>{r.label}</option>
                                        ))}
                                        <option value="custom">
                                            {currentResValue === 'custom'
                                                ? `${data.width} x ${data.height} (custom)`
                                                : 'Custom...'}
                                        </option>
                                    </StyledSelect>
                                    <SelectArrow />
                                </SelectWrapper>
                            </ControlRow>

                            <ControlRow>
                                <ControlLabel>
                                    <LabelText>Refresh Rate</LabelText>
                                    <Tooltip title={t('CMD_REFRESH_DESC')} placement="right" arrow>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            <HelpOutlineIcon style={{ fontSize: 14, opacity: 0.3, cursor: 'help', color: '#fff' }} />
                                        </span>
                                    </Tooltip>
                                </ControlLabel>
                                <SmartValueInput
                                    value={data.refresh}
                                    onChange={(val) => update('refresh', val)}
                                    min={30}
                                    max={500}
                                    step={1}
                                    presets={[60, 75, 120, 144, 165, 240, 360]}
                                    unit="Hz"
                                    showSlider={true}
                                    label="Refresh Rate"
                                />
                            </ControlRow>
                        </VideoGrid>

                        <CheckboxOption
                            label="Force Fullscreen (-fullscreen)"
                            description={t('CMD_FULLSCREEN_DESC')}
                            checked={data.fullscreen}
                            onChange={(val) => update('fullscreen', val)}
                        />
                    </SettingsGroup>
                </SectionBlock>
            </OptionsContainer>
        </PageWrapper>
    );
};

export default LaunchOptionsPage;
